/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo } from "react";
import { useTable, useSortBy, useFilters } from "react-table";

import { Row, Col, Table } from "react-bootstrap";
import Popup from "reactjs-popup";
import { FaSort, FaSortDown, FaSortUp, FaTimes } from "react-icons/fa";
import { EntityHistory } from "../powershadesApiTypeExtensions";

type EntityHistoryModalProps = {
	entityName: string;
	entityHistory: EntityHistory[];
};

// Define a default column filter for text filtering
const DefaultColumnFilter = ({ column: { filterValue, setFilter } }) => (
	<input
		value={filterValue || ""}
		onClick={(e) => e.stopPropagation()}
		onChange={(e) => setFilter(e.target.value || undefined)}
		placeholder="Search..."
		style={{ width: '100%' }}
	/>
);

const EntityHistoryModal = ({ entityName, entityHistory }: EntityHistoryModalProps) => {
	const [isOpen, setIsOpen] = React.useState(false);

	const columns = useMemo(() => [
		{
			Header: 'User',
			accessor: 'user_name',
		},
		{
			Header: 'Email',
			accessor: 'user_email',
		},
		{
			Header: 'Action',
			accessor: 'action',
		},
		{
			Header: 'Previous Value',
			accessor: 'previous_value',
		},
		{
			Header: 'New Value',
			accessor: 'new_value',
		},
		{
			Header: 'Timestamp',
			accessor: 'timestamp',
			Cell: ({ value }) => new Date(value).toLocaleString(),
		}
	], []);

	const tableInstance = useTable(
		{
			columns,
			data: entityHistory,
			defaultColumn: { Filter: DefaultColumnFilter },
		},
		useFilters,
		useSortBy
	);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		rows,
		prepareRow,
	} = tableInstance;

	return (
		<>
			<Row>
				<Col>
					<button
						className="btn btn-primary"
						onClick={() => setIsOpen(true)}
						style={{ marginBottom: '10px' }}
						type="button"
					>
						View History for
						{' '}
						{entityName}
					</button>
				</Col>
			</Row>
			<Popup
				open={isOpen}
				onClose={() => setIsOpen(false)}
				className="edit mobile-modal entity-history-modal"
				modal
				closeOnDocumentClick
			>
				<div style={{ height: '80vh', display: 'flex', flexDirection: 'column' }}>
					<div className='d-flex align-items-start position-relative pb-3' style={{ flexShrink: 0 }}>
						<FaTimes
							className="close-icon link-secondary"
							onClick={() => setIsOpen(false)}
							style={{ marginRight: '1em', flex: '0 0 auto' }}
							cursor='pointer'
						/>
						<h2 style={{ flex: 1, textAlign: 'center', margin: 0 }}>Change History</h2>
					</div>
					
					<div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto', minHeight: 0 }}>
						{' '}
						<Table
							{...getTableProps()}
							size="sm"
							bordered
							hover
							className="table-striped-rows CEDIA"
							style={{ marginBottom: 0 }}
						>
							<thead>
								{headerGroups.map((headerGroup) => (
									<tr {...headerGroup.getHeaderGroupProps()}>
										{headerGroup.headers.map((column) => (
											<th
												{...column.getHeaderProps(column.getSortByToggleProps())}
												style={{
													backgroundColor: '#8dc640',
													color: 'white',
													fontWeight: 'bold',
													cursor: 'pointer',
													padding: '8px'
												}}
											>
												<div
													style={{
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'space-between'
													}}
												>
													<span className="user-select-none">{column.render('Header')}</span>
													<div
														style={{
															display: 'flex',
															flexDirection: 'column',
															justifyContent: 'center',
															marginLeft: '8px'
														}}
													>
														{
															column.isSorted
																? (column.isSortedDesc ? <FaSortDown /> : <FaSortUp />)
																: <FaSort className="opacity-75" />
														}
													</div>
												</div>
												<div>{column.canFilter ? column.render('Filter') : null}</div>
											</th>
										))}
									</tr>
								))}
							</thead>
							<tbody {...getTableBodyProps()}>
								{rows.map((row) => {
									prepareRow(row);
									return (
										<tr {...row.getRowProps()}>
											{row.cells.map((cell) => (
												<td {...cell.getCellProps()}>{cell.render("Cell")}</td>
											))}
										</tr>
									);
								})}
							</tbody>
						</Table>
					</div>
				</div>
			</Popup>
		</>
	);
};

export default EntityHistoryModal;
